import React, { RefObject, useContext, useEffect, useRef, useState } from 'react';

import { CheckCircle } from '@zooplus/icons';
import { CartButton, QuantityPicker } from '@zooplus/ui-components';

import LocResourcesContext from '../../../common/contexts/LocResourcesContext';
import { PDPProduct } from '../../model/BonusPointsProduct';

import PDPAddToCartContext from './PDPAddToCartContext';

type PDPAddToCartRebrandingProps = {
  product: PDPProduct;
  selection: number[];
};
const PDPAddToCartComponent: React.FC<PDPAddToCartRebrandingProps> = ({ product, selection }) => {
  const CSS_PREFIX = 'loy-add-to-cart';
  const { onFormatLocale } = useContext(LocResourcesContext);
  const { redeemableBalance, quantityInCart, onAddToCart } = useContext(PDPAddToCartContext);
  const buttonRef: RefObject<HTMLButtonElement> = useRef<HTMLButtonElement>(null);
  const [selectedQuantity, setSelectedQuantity] = useState<number>(quantityInCart);
  const [adding, setAdding] = useState<boolean>(false);
  const [max, setMax] = useState<number>(9);

  useEffect(() => {
    const currentPoints =
      product.reducedBonuspoints === 0 ? product.bonuspoints : product.reducedBonuspoints;
    setMax(redeemableBalance / currentPoints);
  }, [redeemableBalance]);

  useEffect(() => {
    setSelectedQuantity(quantityInCart);
    setAdding(false);
  }, [quantityInCart]);

  const maxValue = () => {
    return max ? max : 9;
  };

  const defaultSelections = () => {
    const result: number[] = [];
    for (let i = 0; i <= maxValue(); i++) {
      result.push(i);
    }
    return result;
  };

  const handleAmountChange = (quantity: number) => {
    const newValue = Math.max(0, Math.min(quantity, maxValue()));
    setSelectedQuantity(newValue);
  };

  const renderCartFeedBack = () => {
    return (
      <div className={`${CSS_PREFIX}_feed-back-btn`}>
        <CheckCircle />
      </div>
    );
  };
  const renderSelectComponent = () => {
    const selectableArticle = selection !== undefined && selection.length > 0;

    const selections = (selectableArticle ? selection : defaultSelections()).filter(
      (step: number) => step <= maxValue(),
    );
    return (
      <QuantityPicker
        onValueChange={(quantity) => handleAmountChange(quantity)}
        options={selections}
        selectProps={{
          id: `count_${product.articleId}`,
          className: `${CSS_PREFIX}_select-component--quantity-picker`,
        }}
        value={selectedQuantity}
        size="large"
        selectLabel={onFormatLocale?.('bp.productdetail.addtocart.quantitypicker')}
        data-zta={`amount_${product.articleId}`}
      />
    );
  };

  const addToCart = () => {
    if (!adding) {
      const quantityToAdd = selectedQuantity === 0 && quantityInCart === 0 ? 1 : selectedQuantity;
      setAdding(true);
      const node = buttonRef.current;
      onAddToCart(product.articleId, quantityToAdd, node);
    }
  };

  const canRedeemProduct = () => {
    const currentPoints =
      product.reducedBonuspoints === 0 ? product.bonuspoints : product.reducedBonuspoints;
    return redeemableBalance >= currentPoints;
  };

  return canRedeemProduct() ? (
    <div className={`${CSS_PREFIX}`}>
      <div className={`${CSS_PREFIX}_section ${CSS_PREFIX}_select-component`}>
        {renderSelectComponent()}
      </div>
      <div className={`${CSS_PREFIX}_section addtocart`}>
        {adding ? (
          <CartButton
            iconOnly
            isLoading
            loadingIconType="paw"
            size="normal"
            aria-label={onFormatLocale?.('bp.productdetail.addtocart.loadingbutton')}
          />
        ) : (
          <CartButton
            iconOnly
            ref={buttonRef}
            size="normal"
            data-zta={`addtocartbtn_${product.articleId}`}
            aria-label={onFormatLocale?.('bp.productdetail.addtocart.cartbutton')}
            onClick={() => addToCart()}
          />
        )}
        {quantityInCart > 0 && renderCartFeedBack()}
      </div>
    </div>
  ) : (
    <div className={`${CSS_PREFIX}_save-more`}>{onFormatLocale?.('mzp.bonuspoints.savemore')}</div>
  );
};

export default PDPAddToCartComponent;
